import { ActionIcon, Button, Menu, Tooltip, useMantineColorScheme } from '@mantine/core';
import {
  IconArrowLeft,
  IconBulb,
  IconCalendarUser,
  IconFirstAidKit,
  IconLogout,
  IconMenu2,
  IconMoon,
  IconShieldLock,
  IconSun,
  IconUserCircle,
  IconX,
} from '@tabler/icons-react';
import { type CSSProperties, Suspense, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAppearance } from '@/app/appearance/appearance-context';
import { AppIcon } from '@/components/AppIcon';
import { DemoBanner } from '@/components/DemoBanner';
import { useAuth } from '@/features/auth/auth-context';
import { FullPageLoader } from '@/features/auth/guards';
import { usePermissions } from '@/features/auth/permissions';
import { moduleAllowed, useLicence } from '@/features/licensing/licence';
import { LicenceBanner } from '@/features/licensing/LicenceBanner';
import { AssistantProvider, AssistantTopButton } from '@/features/assistant/AssistantLauncher';
import { RecommendationModal } from '@/features/recommendations/RecommendationModal';
import {
  EDITOR_ADMIN_ITEMS,
  EDITOR_GROUPS,
  useIsEditorSpace,
} from '@/features/signup/platform-context';
import { formatCount, usePatientCount } from '@/features/medical-files/count';
import { NotificationBell } from '@/features/notifications/NotificationBell';

import { DASHBOARD, findNavItem, NAV_GROUPS, type NavGroup, type NavItem } from './navigation';

export const APP_NAME = 'GestClinic';
export const APP_VERSION = '1.0';

/** Pages hors menu (compte personnel) : titre et pastille de la barre supérieure. */
const PERSONAL_PAGES: Record<string, Pick<NavItem, 'label' | 'icon' | 'color'>> = {
  '/mon-compte': { label: 'Mon compte et sécurité', icon: IconShieldLock, color: '#475569' },
  '/mon-planning': { label: 'Mon planning', icon: IconCalendarUser, color: '#0891b2' },
};

function initial(user: {
  first_name?: string;
  last_name?: string;
  full_name?: string;
  username: string;
}) {
  const source = user.first_name || user.last_name || user.full_name || user.username || '?';
  return source.trim().charAt(0).toUpperCase() || '?';
}

/**
 * Grande fenêtre d'une rubrique : une tuile par sous-menu.
 * Elle ne se ferme QUE par le bouton X : ni Échap, ni un clic à l'extérieur
 * (un clic à côté fait simplement « signe » vers le bouton X).
 */
function NavLauncher({
  group,
  current,
  onPick,
  onClose,
}: {
  group: NavGroup;
  current?: string;
  onPick: (item: NavItem) => void;
  onClose: () => void;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [nudge, setNudge] = useState(0);
  useEffect(() => {
    const box = boxRef.current;
    box?.querySelector<HTMLElement>('.launch-tile')?.focus();
    // Le focus clavier reste dans la fenêtre (Tab / Maj+Tab bouclent).
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setNudge((n) => n + 1);
        return;
      }
      if (e.key !== 'Tab' || !box) return;
      const focusables = [...box.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')];
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      className="launcher"
      role="dialog"
      aria-modal="true"
      aria-label={group.title}
      onClick={() => setNudge((n) => n + 1)}
    >
      <div className="launch-box" ref={boxRef} onClick={(e) => e.stopPropagation()}>
        <div className="launch-head">
          <AppIcon icon={group.icon} color={group.color} size={44} />
          <h2>{group.title}</h2>
          <span style={{ flex: 1 }} />
          <ActionIcon
            key={nudge}
            className={`launch-close${nudge ? ' nudge' : ''}`}
            variant="default"
            size="xl"
            aria-label="Fermer"
            title="Fermer"
            onClick={onClose}
          >
            <IconX size={20} />
          </ActionIcon>
        </div>
        <div className={`launch-grid n${group.items.length}`}>
          {group.items.map((item, index) => (
            <Link
              key={item.key}
              to={item.to}
              className={`launch-tile${current === item.key ? ' on' : ''}`}
              style={{ '--i': index } as CSSProperties}
              onClick={() => onPick(item)}
            >
              <AppIcon icon={item.icon} color={item.color} size={84} />
              <b>{item.label}</b>
              <span>{item.desc}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const { canAny } = usePermissions();
  const { clinicName } = useAppearance();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const location = useLocation();
  const navigate = useNavigate();
  // Menu mobile ouvert pour une adresse donnée : il se referme de lui-même en changeant de page.
  const [navOpenAt, setNavOpenAt] = useState<string | null>(null);
  const [launch, setLaunch] = useState<NavGroup | null>(null);
  const [recommending, setRecommending] = useState(false);

  const { data: licence } = useLicence();
  const editorSpace = useIsEditorSpace();
  const allowed = (i: NavItem) => (!i.anyOf || canAny(i.anyOf)) && moduleAllowed(licence, i.module);
  // Espace Éditeur : gestion des cliniques et comptes de l'équipe seulement.
  const inSpace = (g: NavGroup, i: NavItem) =>
    !editorSpace ||
    (EDITOR_GROUPS.has(g.title) && (g.title !== 'Administration' || EDITOR_ADMIN_ITEMS.has(i.key)));
  const groups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => allowed(i) && inSpace(g, i)),
  }))
    .filter((g) => g.items.length > 0)
    // Espace Éditeur : sa rubrique en premier.
    .sort(
      (a, b) =>
        Number(editorSpace && b.title === 'Éditeur') - Number(editorSpace && a.title === 'Éditeur'),
    );
  const patientCount = usePatientCount(groups.some((g) => g.countOf === 'patients'));
  const currentItem = findNavItem(location.pathname);
  const personal = PERSONAL_PAGES[location.pathname];
  const head = personal ?? currentItem;

  const navOpen = navOpenAt === location.pathname;
  // Rubrique (à plusieurs menus) de la page affichée : le bouton « Retour » la rouvre.
  const parentGroup = currentItem
    ? groups.find((g) => g.items.length > 1 && g.items.some((i) => i.key === currentItem.key))
    : undefined;

  const openGroup = (group: NavGroup) => {
    // Une rubrique d'un seul menu mène directement à sa page.
    const [only] = group.items;
    if (group.items.length === 1 && only) {
      void navigate(only.to);
      return;
    }
    setLaunch(group);
  };

  return (
    <AssistantProvider>
      <div className={`gc-shell${navOpen ? ' nav-open' : ''}`}>
        <aside className="side-nav" aria-label="Menu latéral">
          <div className="sn-brand">
            <div className="sn-logo">
              <IconFirstAidKit size={26} stroke={2} aria-hidden />
            </div>
            <div>
              <div className="sn-app">{APP_NAME}</div>
              <div className="sn-ecole">{clinicName || 'Gestion de clinique'}</div>
            </div>
          </div>

          <nav className="sn-nav" aria-label="Menu principal">
            {allowed(DASHBOARD) && !editorSpace && (
              <div className="sn-grp">
                <Link
                  to="/"
                  className={`sn-item${currentItem?.key === 'dashboard' ? ' on' : ''}`}
                  aria-current={currentItem?.key === 'dashboard' ? 'page' : undefined}
                >
                  <AppIcon icon={DASHBOARD.icon} color={DASHBOARD.color} />
                  <span>{DASHBOARD.label}</span>
                </Link>
              </div>
            )}
            {groups.map((group) => {
              const active = group.items.find((i) => i.key === currentItem?.key);
              return (
                <button
                  key={group.title}
                  type="button"
                  className={`sn-item sn-rub${active ? ' on' : ''}`}
                  aria-haspopup={group.items.length > 1 ? 'dialog' : undefined}
                  onClick={() => openGroup(group)}
                >
                  <AppIcon icon={group.icon} color={group.color} />
                  <span className="sn-rl">
                    {group.title}
                    {active && <small>{active.label}</small>}
                  </span>
                  <em aria-hidden="true">
                    {group.countOf === 'patients' && patientCount !== undefined
                      ? formatCount(patientCount)
                      : group.items.length}
                  </em>
                </button>
              );
            })}
            {user && !editorSpace && (
              <button
                type="button"
                className="sn-item sn-rub sn-reco"
                aria-haspopup="dialog"
                onClick={() => setRecommending(true)}
              >
                <AppIcon icon={IconBulb} color="#ca8a04" />
                <span className="sn-rl">
                  Recommandations
                  <small>Améliorer le logiciel</small>
                </span>
              </button>
            )}
          </nav>

          {user && (
            <>
              <Menu position="top-start" width={240}>
                <Menu.Target>
                  <button type="button" className="sn-user" aria-label="Menu utilisateur">
                    <span className="sn-av">{initial(user)}</span>
                    <span className="sn-un">
                      <b>{user.full_name || user.username}</b>
                      <span>{(user.role_names ?? user.roles).join(', ') || 'Aucun rôle'}</span>
                    </span>
                  </button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    component={Link}
                    to="/mon-planning"
                    leftSection={<IconCalendarUser size={16} />}
                  >
                    Mon planning
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to="/mon-compte"
                    leftSection={<IconShieldLock size={16} />}
                  >
                    Mon compte et sécurité
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    leftSection={<IconLogout size={16} />}
                    onClick={() => void logout()}
                  >
                    Se déconnecter
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <div className="sn-actions">
                <Button
                  variant="default"
                  size="xs"
                  leftSection={<IconUserCircle size={15} />}
                  component={Link}
                  to="/mon-compte"
                >
                  Mon compte
                </Button>
                <Button
                  variant="default"
                  size="xs"
                  leftSection={<IconLogout size={15} />}
                  onClick={() => void logout()}
                >
                  Déconnexion
                </Button>
              </div>
            </>
          )}
        </aside>
        <div className="nav-backdrop" onClick={() => setNavOpenAt(null)} aria-hidden />

        <div className="main-col">
          <header className="topbar">
            <ActionIcon
              className="nav-toggle"
              variant="default"
              size="lg"
              aria-label="Menu"
              onClick={() => setNavOpenAt(navOpen ? null : location.pathname)}
            >
              <IconMenu2 size={18} />
            </ActionIcon>
            {parentGroup && (
              <Tooltip label={`Revenir à la rubrique ${parentGroup.title}`}>
                <Button
                  className="back-btn"
                  variant="default"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={() => setLaunch(parentGroup)}
                >
                  Retour
                </Button>
              </Tooltip>
            )}
            {head && <AppIcon icon={head.icon} color={head.color} size={34} />}
            <h1 className="page-title">{head?.label ?? APP_NAME}</h1>
            <span style={{ flex: 1 }} />
            {user && !editorSpace && <AssistantTopButton />}
            {user && !editorSpace && (
              <Tooltip label="Vos recommandations au développeur pour améliorer le logiciel">
                <Button
                  className="reco-btn"
                  variant="light"
                  color="yellow"
                  leftSection={<IconBulb size={16} />}
                  onClick={() => setRecommending(true)}
                >
                  Recommandations
                </Button>
              </Tooltip>
            )}
            <NotificationBell />
            <Tooltip label={colorScheme === 'dark' ? 'Thème clair' : 'Thème sombre'}>
              <ActionIcon
                variant="default"
                size="lg"
                onClick={toggleColorScheme}
                aria-label="Changer de thème"
              >
                {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
              </ActionIcon>
            </Tooltip>
          </header>
          <main className="content">
            <DemoBanner onRecommend={() => setRecommending(true)} />
            <LicenceBanner />
            <Suspense fallback={<FullPageLoader />}>
              <Outlet />
            </Suspense>
          </main>
          <footer className="app-foot">
            {APP_NAME} V{APP_VERSION}
            {clinicName ? ` · ${clinicName}` : ''}
          </footer>
        </div>
      </div>
      <RecommendationModal opened={recommending} onClose={() => setRecommending(false)} />
      {launch && (
        <NavLauncher
          group={launch}
          current={currentItem?.key}
          onPick={() => setLaunch(null)}
          onClose={() => setLaunch(null)}
        />
      )}
    </AssistantProvider>
  );
}
